import { useEffectEvent, useRef, useState, type ReactNode } from "react";

import { renderFingerprintingIframe } from "@fluid-app/fluid-pay-core/utils/fingerprinting";
import { type ThreeDSStatus } from "@fluid-app/fluid-pay-core/types";
import { useVgs3DSCompletion, VgsThreeDSModal } from "@fluid-app/fluid-pay-ui";

import useJwt from "@/components/checkout/hooks/useJwt";
import { useTranslation } from "@/hooks/useTranslation";
import { isApiError } from "@/lib/api-helpers";
import { pusherClient } from "@/lib/pusher/pusher-client";
import {
  cardAdd3DSErrorCode,
  confirmPaymentMethod3DS,
  type ConfirmPaymentMethod3DSApproved,
  type ThreeDsRequiredCardAddResponse,
} from "@/networking/payments.api";
import {
  cancelVgs3DSSession,
  fetchVgs3DSSessionStatus,
} from "@/networking/three-ds/vgs.api";

// How long to wait before the single confirm retry when a completion signal
// races the backend's upstream reconcile (422 three_ds_pending).
const PENDING_RETRY_DELAY_MS = 2_000;

const waitFor = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

export interface CardAdd3DSFlowCallbacks {
  /** The session resolved and the payment method exists — run the surface's
   *  success sequence (refetch, close, toast). Awaited, so a returned promise
   *  keeps the surface's spinner up through e.g. a query invalidation when
   *  the approval is frictionless. */
  onApproved: (
    approved: ConfirmPaymentMethod3DSApproved,
  ) => void | Promise<void>;
  /** Terminal failure — surface `message`; keep the card form open so the
   *  customer can retry. */
  onFailed: (message: string) => void;
  /** The customer abandoned the bank check — quiet terminal, no error. */
  onCanceled: () => void;
}

export interface CardAdd3DSFlow {
  /** Drives the session 3DS flow for a `three_ds_required` card-add envelope.
   *  Resolves when the flow reaches a terminal state OR the challenge modal
   *  opens (the modal takes over from there) — drop the surface's spinner
   *  when this resolves. Ignored while another flow is active. */
  begin: (threeDs: ThreeDsRequiredCardAddResponse) => Promise<void>;
  /** Render once in the surface's JSX, next to its other dialogs. */
  modal: ReactNode;
  /** True from begin() until a terminal state. */
  isActive: boolean;
}

interface ActiveCardAdd3DSSession {
  sessionToken: string;
  // Schema-required, but fetchApi's lenient 2xx fallback can deliver an
  // envelope without it — absence engages the completion hook's
  // presence-guard rather than masquerading as a real key.
  merchantTxId: string | undefined;
}

interface PendingChallenge {
  html: string;
  url?: string;
}

/**
 * Orchestrates the cartless (session-scoped) 3DS flow for adding a payment
 * method — shared by every card-add surface (manage profile, checkout saved
 * cards, subscription card swap).
 *
 * `begin(threeDs)` takes the `three_ds_required` envelope from
 * `addCreditCardToCustomer`, runs device fingerprinting when requested, then
 * confirms the session. Four terminal paths:
 *
 * 1. Frictionless — confirm returns `approved` immediately: `onApproved`,
 *    no modal.
 * 2. Challenge — confirm returns `three_ds_challenge`: the challenge modal
 *    opens; the bank's verdict arrives on the Pusher session channel (or via
 *    the status poll), confirm is replayed, and `onApproved` / `onFailed`
 *    fires.
 * 3. Out-of-band — the customer approves in their banking app without
 *    touching the challenge iframe; the backend webhook resolves the session
 *    and the same signal → re-confirm path as (2) completes the flow.
 * 4. Cancel — the customer closes the modal (quiet `onCanceled`) or its
 *    6.5-minute timeout fires (`onFailed`); both also cancel the session
 *    server-side, fire-and-forget.
 *
 * Pending-retry race: a 422 `three_ds_pending` right after a completion
 * signal is benign — the webhook landed but the backend's upstream reconcile
 * is still in flight — so the confirm is replayed exactly once after ~2s.
 *
 * Realtime needs no FE Pusher auth changes: the backend already authorizes
 * `private-3ds-session-*` channels on the existing `/pusher/auth` endpoint.
 */
export function useCardAdd3DSFlow(
  callbacks: CardAdd3DSFlowCallbacks,
): CardAdd3DSFlow {
  const { jwt } = useJwt();
  const { t } = useTranslation();

  const [session, setSession] = useState<ActiveCardAdd3DSSession | null>(null);
  const [challenge, setChallenge] = useState<PendingChallenge | null>(null);
  // Synchronous mirror of `session` for the single-flight guard and async
  // continuations (state reads inside promise chains would be stale).
  const activeSessionRef = useRef<ActiveCardAdd3DSSession | null>(null);

  // Surfaces pass inline closures — these wrappers always call the latest.
  const emitApproved = useEffectEvent(
    (approved: ConfirmPaymentMethod3DSApproved) =>
      callbacks.onApproved(approved),
  );
  const emitFailed = useEffectEvent((message: string) =>
    callbacks.onFailed(message),
  );
  const emitCanceled = useEffectEvent(() => callbacks.onCanceled());

  const resetFlow = () => {
    activeSessionRef.current = null;
    setSession(null);
    setChallenge(null);
  };

  // Exactly-one-terminal guard, centralized: each finish* helper no-ops once
  // a terminal has already won (the winning call always enters with the ref
  // still set, because the ref is only nulled here, via resetFlow, right
  // before the emit). This also covers entry points the per-await staleness
  // checks can't reach — the modal's timer/Cancel invoking onClose in the
  // window between a terminal's reset and React committing the modal
  // unmount, and begin()'s catch firing after the surface's own onApproved
  // rejected mid-finishApproved.
  const finishApproved = async (approved: ConfirmPaymentMethod3DSApproved) => {
    if (!activeSessionRef.current) return;
    resetFlow();
    await emitApproved(approved);
  };

  const finishFailed = (message: string) => {
    if (!activeSessionRef.current) return;
    resetFlow();
    emitFailed(message);
  };

  const finishCanceled = () => {
    if (!activeSessionRef.current) return;
    resetFlow();
    emitCanceled();
  };

  const failureMessageFrom = (error: unknown): string => {
    if (cardAdd3DSErrorCode(error) === "three_ds_session_not_found") {
      // The session evaporated (expired, or never minted for this customer) —
      // the timeout message is the closest honest description.
      return t("payment_verification_timed_out");
    }
    if (isApiError(error) && error.data?.error_message) {
      // covers three_ds_failed / card_verification_failed (and a still-pending
      // retry): the backend's error_message is the most specific text we have.
      return error.data.error_message;
    }
    return t("payment_verification_didnt_complete");
  };

  const begin = async (
    threeDs: ThreeDsRequiredCardAddResponse,
  ): Promise<void> => {
    // Single-flight: one bank check at a time.
    if (activeSessionRef.current) return;

    // Defensive: fetchApi's lenient 2xx fallback can let a malformed envelope
    // through Zod — never call confirm with an empty session token. Emits
    // directly (not via finishFailed): no attempt was activated yet, so the
    // finish* guard — which requires an active ref — would swallow this.
    if (!threeDs.session_token || !jwt) {
      emitFailed(t("payment_verification_didnt_complete"));
      return;
    }

    const active: ActiveCardAdd3DSSession = {
      sessionToken: threeDs.session_token,
      merchantTxId: threeDs.merchant_tx_id,
    };
    activeSessionRef.current = active;
    setSession(active);

    try {
      // Device fingerprinting (3DS Method): hidden iframe, ~10s. Skipped
      // only when the backend explicitly opts out or sent no method content.
      // Inside the try so even a synchronous throw lands on finishFailed —
      // an escape would leave activeSessionRef set and deadlock single-flight.
      if (
        threeDs.fingerprinting_required !== false &&
        typeof threeDs.tds_method_content === "string" &&
        threeDs.tds_method_content.length > 0
      ) {
        await renderFingerprintingIframe(threeDs.tds_method_content);
      }

      const confirmation = await confirmPaymentMethod3DS({
        jwt,
        sessionToken: active.sessionToken,
      });

      if (confirmation.status === "approved") {
        await finishApproved(confirmation);
        return;
      }

      // Challenge required: open the modal and hand over to the completion
      // hook — begin() resolves here so the surface can drop its spinner.
      // A challenge envelope with no form would render an empty modal that
      // strands the user until the hard timeout — fail fast instead.
      if (!confirmation.challenge_form) {
        finishFailed(t("payment_verification_didnt_complete"));
        return;
      }
      setChallenge({
        html: confirmation.challenge_form,
        url: confirmation.challenge_url ?? undefined,
      });
    } catch (error) {
      finishFailed(failureMessageFrom(error));
    }
  };

  // Replays confirm after a completion signal (Pusher event or status poll).
  //
  // The modal stays open while this runs (its Cancel button and 6.5-minute
  // timeout stay live), so a cancel/timeout terminal can fire mid-await.
  // Every await is therefore followed by a staleness check against the
  // attempt captured at entry: once another terminal won, this replay must
  // go silent — the flow lands on EXACTLY ONE of onApproved/onFailed/
  // onCanceled.
  const reconfirmAfterSignal = async (
    allowPendingRetry: boolean,
  ): Promise<void> => {
    const active = activeSessionRef.current;
    // Only reachable when a cancel/timeout terminal raced the completion
    // signal (or jwt evaporated) — a terminal already fired or the modal's
    // timeout will provide one. Stay silent; never double-fire.
    if (!active || !jwt) return;

    try {
      const confirmation = await confirmPaymentMethod3DS({
        jwt,
        sessionToken: active.sessionToken,
      });
      if (activeSessionRef.current !== active) return;

      if (confirmation.status === "approved") {
        await finishApproved(confirmation);
        return;
      }

      // A second challenge envelope after a completion signal is out of
      // contract — fail rather than loop the modal.
      finishFailed(t("payment_verification_didnt_complete"));
    } catch (error) {
      if (activeSessionRef.current !== active) return;

      if (
        allowPendingRetry &&
        cardAdd3DSErrorCode(error) === "three_ds_pending"
      ) {
        await waitFor(PENDING_RETRY_DELAY_MS);
        if (activeSessionRef.current !== active) return;
        await reconfirmAfterSignal(false);
        return;
      }
      finishFailed(failureMessageFrom(error));
    }
  };

  // Poll mapping from the session status endpoint to the completion hook's
  // ThreeDSStatus contract.
  const sessionStatusSnapshot = async (): Promise<ThreeDSStatus | null> => {
    const active = activeSessionRef.current;
    if (!active || !jwt) return null;

    try {
      const snapshot = await fetchVgs3DSSessionStatus({
        jwt,
        sessionToken: active.sessionToken,
      });
      return {
        // `skipped` = acquirer network unconfigured; the backend degraded to
        // a plain verify and confirm returns approved + PM — a success.
        completed:
          snapshot.state === "approved" || snapshot.state === "skipped",
        failed:
          snapshot.state === "denied" ||
          snapshot.state === "timed_out" ||
          snapshot.expired,
        canceled: snapshot.state === "cancelled",
        // The status response carries no merchant_tx_id; leaving this
        // undefined engages the completion hook's presence-guard (the
        // session token already scopes the attempt).
        merchantTxId: undefined,
        status: snapshot.state,
        errorMessage: undefined,
      };
    } catch {
      // No info — keep polling (transient fetch errors and 422s alike).
      return null;
    }
  };

  useVgs3DSCompletion({
    channelName: session ? `private-3ds-session-${session.sessionToken}` : "",
    transport: pusherClient,
    checkStatus: sessionStatusSnapshot,
    merchantTxId: session?.merchantTxId,
    enabled: challenge !== null,
    onComplete: () => {
      void reconfirmAfterSignal(true);
    },
    onError: (message) => finishFailed(message),
    onCanceled: finishCanceled,
  });

  // Timeout path arrives with an error string; user-cancel arrives without.
  const closeChallengeModal = (timeoutError?: string) => {
    const active = activeSessionRef.current;
    // Fire-and-forget server-side cancel — the local close never waits on it
    // and never fails because of it (incl. the 5s AbortError); the backend
    // expires the session on its own regardless.
    if (active && jwt) {
      cancelVgs3DSSession({ jwt, sessionToken: active.sessionToken }).catch(
        () => {},
      );
    }

    if (timeoutError) {
      finishFailed(timeoutError);
    } else {
      finishCanceled();
    }
  };

  const modal = (
    <VgsThreeDSModal
      isOpen={challenge !== null}
      challengeHtml={challenge?.html ?? ""}
      challengeUrl={challenge?.url}
      onClose={closeChallengeModal}
      labels={{
        title: t("verify_your_payment"),
        cancel: t("cancel"),
        timedOut: t("payment_verification_timed_out"),
        stillWorking: t("still_working_with_your_bank"),
        dontRefresh: t("dont_refresh_this_tab"),
      }}
    />
  );

  return { begin, modal, isActive: session !== null };
}
